import React from 'react';
import { Box, Typography, Container, Chip, useMediaQuery, useTheme } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useSwipeable } from 'react-swipeable';
import { useSelector } from 'react-redux';
import { StyledCalendarDay, StyledEventCard } from '../../styles/styledComponents';
import FilterListIcon from '@mui/icons-material/FilterList';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const Calendar = ({
  currentDate,
  currentTournaments,
  isLoading,
  slideDirection,
  onMonthChange,
  onEventClick,
  today
}) => {
  const filters = useSelector(state => state.filters);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => onMonthChange('next'),
    onSwipedRight: () => onMonthChange('prev'),
    preventDefaultTouchmoveEvent: true,
    trackMouse: false
  });

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const renderSchedule = (day) => {
    const currentDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const todayStart = new Date(today);
    todayStart.setHours(0, 0, 0, 0);
    const isPastDay = currentDay < todayStart;

    // フィルタリング適用
    const tournaments = currentTournaments[day] || [];
    const filteredDayTournaments = filters.isActive ? 
      tournaments.filter(tournament => {
        // 大会名フィルター
        const matchesTournament = !filters.tournamentNames?.length || 
          filters.tournamentNames.includes(tournament.tournamentName || tournament.title);
        
        // チーム名フィルター
        const matchesTeam = !filters.teamNames?.length || 
          tournament.matchups?.some(matchup => 
            matchup.teams.some(team => filters.teamNames.includes(team.name))
          );
        
        // ゲームタイトルフィルター
        const matchesGame = !filters.gameTitles?.length || 
          filters.gameTitles.includes(tournament.category);
        
        return matchesTournament && matchesTeam && matchesGame;
      }) : tournaments;

    // 開始時間順にソート
    const sortedTournaments = [...filteredDayTournaments].sort((a, b) => {
      // timeフィールドから開始時間を抽出 ("HH:MM - HH:MM" 形式)
      const getStartTime = (tournament) => {
        if (!tournament.time) return '99:99'; // timeがない場合は最後に配置
        const startTime = tournament.time.split(' - ')[0];
        return startTime || '99:99';
      };
      
      const timeA = getStartTime(a);
      const timeB = getStartTime(b);
      
      return timeA.localeCompare(timeB);
    });

    // フィルターが有効で、フィルター後の大会が0件の場合は何も表示しない
    if (filters.isActive && tournaments.length > 0 && sortedTournaments.length === 0) {
      return null;
    }

    return sortedTournaments.length > 0 ? sortedTournaments.map((tournament, index) => (
      <StyledEventCard
        key={tournament.id}
        isPast={isPastDay}
        onClick={() => onEventClick(tournament)}
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ 
          duration: 0.4, 
          ease: [0.25, 0.1, 0.25, 1],
          delay: index * 0.08
        }}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
      >
        <Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EventIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'primary.main', 
                  fontWeight: 600,
                  fontSize: isMobile ? '0.8rem' : '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}
              >
                {tournament.category}
              </Typography>
            </Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontSize: isMobile ? '1rem' : '1.125rem', 
                fontWeight: 700, 
                color: 'text.primary',
                lineHeight: 1.3,
                mb: 0.5
              }}
            >
              {tournament.tournamentName || tournament.title}
            </Typography>
            {tournament.matchups && tournament.matchups.map((matchup, idx) => (
              <Box key={idx} sx={{ 
                p: 1, 
                bgcolor: 'grey.50', 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200'
              }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: isMobile ? '0.875rem' : '1rem', 
                    color: 'text.primary', 
                    fontWeight: 600,
                    lineHeight: 1.4,
                    textAlign: 'center'
                  }}
                >
                  {matchup.teams[0].name} <span style={{ color: theme.palette.primary.main }}>vs</span> {matchup.teams[1].name}
                </Typography>
              </Box>
            ))}
            {tournament.time && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  {tournament.time}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </StyledEventCard>
    )) : null;
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const days = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const isToday = 
        currentDate.getFullYear() === today.getFullYear() &&
        currentDate.getMonth() === today.getMonth() &&
        day === today.getDate();
      
      if (!currentTournaments[day] && !isToday) continue;

      const dayOfWeek = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day
      ).getDay();

      const dayNames = ['日', '月', '火', '水', '木', '金', '土'];

      const dateStr = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      
      days.push(
        <StyledCalendarDay
          key={day}
          data-is-today={isToday}
          data-date={dateStr}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 0.6,
            ease: [0.25, 0.1, 0.25, 1],
            delay: days.length * 0.08
          }}
        >
          <Box className="day-number">
            <Box className="date-circle">
              <Typography 
                variant="body1" 
                fontWeight={isToday ? 700 : 600}
                color="inherit"
              >
                {day}
              </Typography>
            </Box>
            <Box>
              <Typography 
                variant="body1" 
                color={dayOfWeek === 0 ? 'error.main' : dayOfWeek === 6 ? 'info.main' : 'text.secondary'}
                fontWeight={600}
                sx={{ fontSize: isMobile ? '0.85rem' : '1rem' }}
              >
                {dayNames[dayOfWeek]}曜日
              </Typography>
              {isToday && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'primary.main',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}
                >
                  Today
                </Typography>
              )}
            </Box>
          </Box>
          <Box 
            className="events-container" 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: isMobile ? 0.5 : 0.75
            }}
          >
          {currentTournaments[day] ? renderSchedule(day) : (
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary', 
                textAlign: 'center', 
                py: isMobile ? 1.5 : 2,
                fontSize: isMobile ? '0.8rem' : '0.875rem'
              }}
            >
              {isToday ? '本日の予定はありません' : '予定はありません'}
            </Typography>
          )}
          </Box>
        </StyledCalendarDay>
      );
    }

    return days;
  };

  return (
    <Container maxWidth="sm" sx={{ px: isMobile ? 1 : 2 }}>
      {filters.isActive && !isMobile && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <FilterListIcon sx={{ color: 'primary.main', fontSize: 20 }} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            フィルター適用中:
          </Typography>
          {filters.tournamentNames.length > 0 && (
            <Chip 
              label={`大会名 ${filters.tournamentNames.length}件`} 
              size="small" 
              color="primary" 
              variant="outlined"
            />
          )}
          {filters.teamNames.length > 0 && (
            <Chip 
              label={`チーム ${filters.teamNames.length}件`} 
              size="small" 
              color="secondary" 
              variant="outlined"
            />
          )}
          {filters.gameTitles.length > 0 && (
            <Chip 
              label={`ゲーム ${filters.gameTitles.length}件`} 
              size="small" 
              color="success" 
              variant="outlined"
            />
          )}
        </Box>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', minHeight: '200px' }}>
        <AnimatePresence mode="wait">
          {isLoading ? (
            <motion.div
              key="skeleton"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{ width: '100%' }}
            >
              {[1, 2, 3].map((i) => (
                <StyledCalendarDay
                  key={i}
                  sx={{
                    mb: isMobile ? 1.5 : 2,
                    p: isMobile ? 1.5 : 2,
                    borderRadius: 1,
                    bgcolor: 'grey.100',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                      '100%': { opacity: 1 },
                    },
                  }}
                >
                  <Box className="day-number">
                    <Box className="date-circle">
                      <Box sx={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        bgcolor: 'grey.200'
                      }} />
                    </Box>
                    <Box sx={{
                      width: '60%',
                      height: isMobile ? 20 : 24,
                      bgcolor: 'grey.200',
                      borderRadius: 1
                    }} />
                  </Box>
                  <Box className="events-container">
                    <Box sx={{
                      p: isMobile ? 1.5 : 2,
                      borderRadius: 1,
                      bgcolor: 'grey.200',
                      mb: isMobile ? 1 : 2,
                      height: isMobile ? 60 : 80
                    }} />
                    <Box sx={{
                      p: isMobile ? 1.5 : 2,
                      borderRadius: 1,
                      bgcolor: 'grey.200',
                      height: isMobile ? 60 : 80
                    }} />
                  </Box>
                </StyledCalendarDay>
              ))}
            </motion.div>
          ) : (
            <motion.div
              {...swipeHandlers}
              key={currentDate.getMonth()}
              initial={{ 
                x: slideDirection === 'left' ? 200 : slideDirection === 'right' ? -200 : 0,
                opacity: 0 
              }}
              animate={{ 
                x: 0,
                opacity: 1 
              }}
              exit={{ 
                x: slideDirection === 'left' ? -200 : slideDirection === 'right' ? 200 : 0,
                opacity: 0,
                position: 'absolute'
              }}
              transition={{
                type: "spring",
                stiffness: 100,
                damping: 20,
                mass: 0.8,
                opacity: { duration: 0.3 }
              }}
              style={{
                width: '100%',
                touchAction: 'pan-y pinch-zoom'
              }}
            >
              {renderCalendar()}
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </Container>
  );
};

export default Calendar;
